import { useEffect, useRef, useState } from 'react';
import { uniformDashboardAPI } from '../../services/api';
import { Modal } from './DashboardPrimitives';
import { InlineLoader } from '../loading';
import {
  buildKitView,
  classifyFailure,
  kitStockMark,
  type UniformKitLine,
  type UniformKitSection,
  type UniformLoadFailure,
  type UniformRequestRow,
} from './uniformDashboard';

/**
 * Dashboard → Uniform & Asset Management: View kit.
 *
 * One request's kit lines exactly as 1Office Admin Management's Dashboard shows
 * them — name · kit · quarter, then Item / Size / Qty / Stock — read-only. The lines
 * come from the same Admin Management function its own screen uses, so the two
 * cannot disagree; this component only lays them out.
 *
 * The API answers only for a request that sits on `projectId` (the upstream check,
 * "Request not found in your projects"), so a row from one project can never be
 * opened against another — and the tab closes this modal on a project switch anyway.
 *
 * A 403 is not this modal's to show. It is the access answer for the whole tab (the
 * grant was withdrawn while the tab was open), so it is handed to `onDenied` and the
 * tab closes the modal, takes the project's rows off the screen and shows its access
 * panel. Printing it here as a red error would leave every request of the project
 * standing behind a dialog that says the reader may not see them.
 */
export default function UniformKitModal({
  projectId,
  row,
  onClose,
  onDenied,
}: {
  projectId: number;
  row: UniformRequestRow;
  onClose: () => void;
  /** The API answered 403: the tab's access answer, with the server's message. */
  onDenied: (failure: Extract<UniformLoadFailure, { kind: 'denied' }>) => void;
}) {
  const [view, setView] = useState<{ description: string | null; sections: UniformKitSection[] } | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Through a ref, so the load below does not run again each time the tab re-renders
  // and hands over a new function.
  const onDeniedRef = useRef(onDenied);
  onDeniedRef.current = onDenied;

  useEffect(() => {
    // Only the answer for the request on screen may land: the modal can be closed,
    // or reopened on another row, while this is still in flight.
    let alive = true;
    setView(null);
    setError(null);
    uniformDashboardAPI
      .getKit({ projectId, requestId: row.request_id })
      .then((res) => {
        if (alive) setView(buildKitView(row, res.data?.data?.kits));
      })
      .catch((err) => {
        if (!alive) return;
        const failure = classifyFailure(err, 'Could not load the kit lines.');
        if (failure.kind === 'denied') onDeniedRef.current(failure);
        else setError(failure.message);
      });
    return () => { alive = false; };
  }, [projectId, row]);

  return (
    <Modal title={`Dispatch Details (${row.request_code})`} onClose={onClose} maxWidth="max-w-3xl">
      {error ? (
        <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700" role="alert">{error}</div>
      ) : view === null ? (
        <p className="py-10 text-center text-sm text-gray-500"><InlineLoader label="Loading…" className="flex justify-center" /></p>
      ) : (
        <>
          {view.description && <p className="mb-3 text-sm text-gray-600">{view.description}</p>}
          {view.sections.length === 0 ? (
            <p className="py-10 text-center text-sm text-gray-500">No kit lines are recorded for this request.</p>
          ) : (
            view.sections.map((s) => (
              <div key={s.key}>
                {s.heading && <p className="mb-2 text-sm font-medium text-gray-700">{s.heading}</p>}
                <KitItemsTable items={s.items} ufMode={s.ufMode} requestStatus={s.requestStatus} />
              </div>
            ))
          )}
        </>
      )}
    </Modal>
  );
}

const TH = 'px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-gray-500';
// Padding and colour are kept apart on purpose. Tailwind writes its text-colour
// utilities out in alphabetical order, so on one element `text-gray-700` BEATS
// `text-amber-600` (and loses to `text-red-600`) whatever order the class names are
// written in — a cell that needs its own colour must not carry the default one too.
const CELL = 'px-3 py-2 text-sm';
const TD = `${CELL} text-gray-700`;
const MARK = 'inline-block rounded px-2 py-0.5 text-center text-[10px] font-semibold uppercase leading-tight';

/**
 * A kit's Item / Size / Qty / Stock table — the same wording as Admin Management's,
 * so a request reads the same on both screens.
 *
 * Size is the one cell that shouts: a line with no size at all is red "no size", and
 * a size Admin Management has marked `invalid_size` is amber with "(invalid)" after
 * it. The Stock cell follows kitStockMark — see it for when a line is marked at all.
 */
function KitItemsTable({
  items,
  ufMode,
  requestStatus,
}: {
  items: UniformKitLine[];
  ufMode: string | null;
  requestStatus: number | null;
}) {
  return (
    <div className="mb-4 overflow-hidden rounded-lg border border-gray-100">
      <table className="min-w-full">
        <thead>
          <tr className="border-b border-gray-100 bg-gray-50">
            <th className={TH}>Item</th>
            <th className={TH}>Size</th>
            <th className={TH}>Qty</th>
            <th className={TH}>Stock</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-50">
          {items.map((it, idx) => {
            const invalid = !!it.size && it.size_status === 'invalid_size';
            const mark = kitStockMark(it, ufMode, requestStatus);
            return (
              // Index in the key: a line id is not guaranteed unique across a
              // request's kits, and a repeated key would silently drop a line.
              <tr key={`${it.id}:${idx}`}>
                <td className={TD}>{it.item_name}</td>
                <td className={`${CELL} ${!it.size ? 'font-semibold text-red-600' : invalid ? 'font-semibold text-amber-600' : 'text-gray-700'}`}>
                  {it.size || 'no size'}{invalid ? ' (invalid)' : ''}
                </td>
                <td className={`${TD} tabular-nums`}>{it.quantity}</td>
                <td className={TD}>
                  {mark === 'none' ? (
                    <span className="text-gray-400">—</span>
                  ) : mark === 'no_stock_item' ? (
                    <span
                      className={`${MARK} bg-amber-100 text-amber-700`}
                      title="This line has no stock item, so it can never be drawn — adding inventory will not clear it. A legacy or ID-card-only row."
                    >
                      no stock item
                    </span>
                  ) : (
                    <span
                      className={`${MARK} ${mark === 'not_drawn_shipped' ? 'bg-red-100 text-red-700' : 'bg-slate-100 text-slate-600'}`}
                      title={mark === 'not_drawn_shipped'
                        ? 'Never drawn from stock — the parcel went without this garment.'
                        : 'Not drawn from stock yet. Normal at this stage; the draw runs at booking or processing.'}
                    >
                      not drawn
                    </span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
